import { AxiosResponse } from 'axios';
import { API_ROUTES } from '../ApiRoutes';
import instance, { GET } from '../services/api';
import {
  CompaniesEmployeesPagination,
  CompanyEmployeeCreate,
  CompanyEmployeeInfo,
} from '../models/company-employee.model';
import { IListRequest } from 'data/models/common.model';
import { buildApiPath } from 'utils';
import { GridSortDirection } from '@mui/x-data-grid';

export interface ICompanyUserListRequest {
  query?: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  order?: GridSortDirection;

  isAdmin?: string[];
  startDate?: string;
  endDate?: string;
  isActive?: string[];
  tags?: string[];
  companies?: string[];
  branchCompanies?: string[];
  hasBranchCompanies?: boolean;

  eventType?: string;
  phoneStatus?: string[];
}

export const getCompaniesEmployeesPagination = async (
  companyId: string,
  adminOnly: boolean,
  page: number,
  sortBy?: string,
  order?: string,
  pageSize?: number,
): Promise<CompaniesEmployeesPagination> => {
  const data: AxiosResponse<CompaniesEmployeesPagination> = await instance
    .get(
      API_ROUTES.companiesEmployees +
        `?companyId=${companyId}&adminOnly=${adminOnly}&page=${page.toString()}&sortBy=${sortBy}&order=${order}&pageSize=${
          pageSize || 10
        }`,
    )
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const createCompanyEmployee = async (
  payload: CompanyEmployeeCreate,
): Promise<CompanyEmployeeInfo> => {
  const data: AxiosResponse<CompanyEmployeeInfo> = await instance
    .post(API_ROUTES.companiesEmployees, payload)
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const uploadEmployeesCSV = async (
  companyId: string,
  file: File,
): Promise<{ success: boolean; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const data: AxiosResponse<{ success: boolean; message: string }> =
    await instance
      .post(API_ROUTES.companiesEmployeesCSVCompanyId(companyId), formData, {
        headers: {
          'Content-Type': file.type,
        },
      })
      .catch((error) => {
        throw error;
      });

  return data.data;
};

export interface ImportMemberEventRowResult {
  rowNumber: number;
  row: Record<string, string | undefined>;
  status: 'created' | 'matched' | 'error';
  eventCreated: boolean;
  errors: string[];
  warnings: string[];
}

export interface ImportMemberEventSummary {
  rowsProcessed: number;
  membersCreated: number;
  membersMatched: number;
  eventsCreated: number;
  rowsErrored: number;
  rowsWarned: number;
}

export interface ImportMemberEventResult {
  summary: ImportMemberEventSummary;
  rows: ImportMemberEventRowResult[];
}

export interface ImportMemberEventResponse {
  success: boolean;
  message: string;
  data?: ImportMemberEventResult;
}

export const isValidImportMemberEventResult = (
  data: unknown,
): data is ImportMemberEventResult => {
  if (!data || typeof data !== 'object') return false;
  const summary = (data as { summary?: unknown }).summary;
  if (!summary || typeof summary !== 'object') return false;
  const s = summary as Record<string, unknown>;
  const summaryFields = [
    'rowsProcessed',
    'membersCreated',
    'membersMatched',
    'eventsCreated',
    'rowsErrored',
    'rowsWarned',
  ];
  if (!summaryFields.every((field) => typeof s[field] === 'number')) {
    return false;
  }
  const rows = (data as { rows?: unknown }).rows;
  if (!Array.isArray(rows)) return false;
  return rows.every((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    const r = row as Record<string, unknown>;
    if (typeof r.rowNumber !== 'number') return false;
    if (!r.row || typeof r.row !== 'object' || Array.isArray(r.row)) {
      return false;
    }
    if (
      !Array.isArray(r.errors) ||
      !r.errors.every((error) => typeof error === 'string')
    ) {
      return false;
    }
    if (
      !Array.isArray(r.warnings) ||
      !r.warnings.every((warning) => typeof warning === 'string')
    ) {
      return false;
    }
    return true;
  });
};

// NOTE: unlike uploadEmployeesCSV, we do not set a Content-Type header — the
// browser must set multipart/form-data with its own boundary. The auth
// interceptor still applies to the default headers.
export const uploadMemberEventCSV = async (
  companyId: string,
  file: File,
): Promise<ImportMemberEventResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const data: AxiosResponse<ImportMemberEventResponse> = await instance
    .post(API_ROUTES.importMemberEventCSVCompanyId(companyId), formData)
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getCompanyUsers = async (
  companyId: string,
  params: ICompanyUserListRequest,
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await GET(
      buildApiPath({
        path: API_ROUTES.getCompaniesUsers(companyId),
        params: params,
      }),
    );
    return response.data;
  } catch (e: unknown) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const getAllUsers = async (
  params: ICompanyUserListRequest,
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await GET(
      buildApiPath({
        path: API_ROUTES.getAllUsers,
        params: params,
      }),
    );
    return response.data;
  } catch (e: unknown) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};
